import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { jobs, type JobView } from '../lib/api';
import { useTranslation } from '../i18n';
import { EmptyState } from '../components/ui';

const TERMINAL = ['succeeded', 'failed', 'partial', 'cancelled'];

export default function JobPage() {
  const { t } = useTranslation();
  const { jobId = '' } = useParams();
  const [job, setJob] = useState<JobView | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const j = await jobs.get(jobId);
        if (!alive) return;
        setJob(j);
        setError(false);
        if (!TERMINAL.includes(j.status)) timer = setTimeout(poll, 4000);
      } catch {
        if (!alive) return;
        setError(true);
        timer = setTimeout(poll, 6000);
      }
    }
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [jobId]);

  const pct = Math.round((job?.progress ?? 0) * 100);
  const done = !!job && ['succeeded', 'partial'].includes(job.status);
  const failed = job?.status === 'failed';

  return (
    <main className="container job-page">
      <h1 className="job-title">{t('job.title')}</h1>

      {!job && !error && <p className="muted">{t('common.loading')}</p>}

      {job && (
        <>
          <div className="job-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="job-bar__fill" style={{ width: `${failed ? 100 : pct}%` }} data-failed={failed} />
          </div>
          <div className="job-meta">
            <span className="job-pct">{pct}%</span>
            <span>{t('job.stage')}: {job.stage ?? '—'}</span>
            <span>{t('job.cost')}: ${Number(job.total_cost_usd ?? 0).toFixed(2)}</span>
          </div>

          {!done && !failed && (
            <p className="job-note">{t('job.processing')}<br />{t('job.eta')}</p>
          )}

          {done && (
            <div className="job-actions">
              <p className="job-done">✓ {t('job.done')}</p>
              <Link to={`/books/${job.book_id}`} className="btn btn-accent">{t('job.openBook')}</Link>
            </div>
          )}

          {failed && (
            <EmptyState
              tone="error"
              title={t('job.failed')}
              description={job.error ? String((job.error as { message?: string }).message ?? JSON.stringify(job.error)).slice(0, 200) : undefined}
              primary={<Link to={`/books/${job.book_id}`} className="btn btn-secondary">{t('job.openBook')}</Link>}
            />
          )}
        </>
      )}
    </main>
  );
}
